import * as t from 'io-ts';
import {
  Template,
  MySafetyHtmls,
  TopRoutes,
  CabData,
  CabTypesAvailableForRent,
  RouteInfo,
  NameUrl,
  LeaveIntentOffer,
  RouteCarTypeInfo,
  HotelsInfoType,
  TripBenefit,
  InterlinkBlockDataChecker,
  AggregateRating,
  PackageDataChecker
} from '../runtime-typechecker';
import {
  ORDER_BY_PRICE_ASC,
  ORDER_BY_RATING_DESC
} from '../../constants/miscellaneousConstants';

export const CityDataForRoute = t.type({
  id: t.number,
  name: t.string,
  description: t.union([t.string, t.null]),
  latitude: t.string,
  longitude: t.string,
  code: t.string,
  placeId: t.string
});
export const TopRoutesInfoFromSourceToDestination = t.type({
  oneWayDurationInHrMins: t.string,
  oneWayDistance: t.number,
  seoUrl: t.union([t.undefined, t.null, t.string]),
  cabsList: t.array(CabData),
  tripBenefits: t.union([t.array(TripBenefit), t.null])
});
export type TopRoutesInfoFromSourceToDestination = t.TypeOf<
  typeof TopRoutesInfoFromSourceToDestination
>;
export const RTC = t.type({
  name: t.string,
  acronym: t.string,
  seoUrl: t.string
});

const RailsRouteInfo = t.type({
  source: t.union([t.string, t.undefined]),
  destination: t.union([t.string, t.undefined]),
  routePageUrl: t.string
});

export const Filter = t.type({
  fieldName: t.string,
  filterName: t.string,
  filterValues: t.array(t.string)
});
export const CouponData = t.type({
  couponCode: t.string,
  subTitle: t.string,
  title: t.string
});
export type Filter = t.TypeOf<typeof Filter>;

export const UgcReviewsData = t.type({
  numberOfRating: t.union([t.number, t.undefined]),
  driverRating: t.union([t.number, t.undefined]),
  vehicleRating: t.union([t.number, t.undefined]),
  uniquePositiveTags: t.union([t.array(t.string), t.undefined, t.null]),
  reviewCount: t.union([t.number, t.undefined]),
  bestReviews: t.union([t.any, t.undefined, t.null])
});

export type UgcReviewsData = t.TypeOf<typeof UgcReviewsData>;

export const RouteTemplateChecker = t.type({
  packages: t.union([t.array(PackageDataChecker), t.undefined]),
  filters: t.union([t.array(Filter), t.undefined, t.null]),
  forwardDateCount: t.union([t.undefined, t.null, t.number]),
  isomorph: t.union([t.boolean, t.undefined, t.null]),
  id: t.number,
  sourceCityId: t.number,
  destinationCityId: t.number,
  isActive: t.boolean,
  isPopular: t.boolean,
  url: t.string,
  reviewLinkEnabled: t.union([t.boolean, t.null]),
  description: t.union([t.string, t.null]),
  htmlRefreshDate: t.union([t.string, t.null]),
  created: t.string,
  modified: t.string,
  templateData: Template,
  sourceCityData: CityDataForRoute,
  destinationCityData: CityDataForRoute,
  topRoutesfromCity: TopRoutes,
  topRoutesToCity: TopRoutes,
  topRoutesfromAirport: TopRoutes,
  topRoutesToAirport: TopRoutes,
  topRoutesInfoFromSourceToDestination: TopRoutesInfoFromSourceToDestination,
  topRoutesInfoFromSourceToDestinationIsomorphic: t.union([
    TopRoutesInfoFromSourceToDestination,
    t.undefined,
    t.null
  ]),
  cabTypesAvailableForRent: CabTypesAvailableForRent,
  mySafetyHtmls: t.union([t.undefined, MySafetyHtmls]),
  otherRoutesFromSource: t.union([t.array(RouteInfo), t.undefined]),
  otherRoutesToDestination: t.union([t.array(RouteInfo), t.undefined]),
  nearbyCities: t.union([t.undefined, t.array(NameUrl)]),
  otherPopularRoutes: t.array(RouteInfo),
  leaveIntentOffers: t.union([t.array(LeaveIntentOffer), t.undefined]),
  busRTCs: t.array(RTC),
  randomBusRoutesToDestination: t.array(RouteInfo),
  randomBusRoutesFromSource: t.array(RouteInfo),
  routeCarTypeRoutesFromSource: t.array(RouteCarTypeInfo),
  routeCarTypeRoutesToDestination: t.array(RouteCarTypeInfo),
  trainsStartingFromSource: t.union([
    t.array(RailsRouteInfo),
    t.undefined,
    t.null
  ]),
  trainsEndingAtDestination: t.union([
    t.array(RailsRouteInfo),
    t.undefined,
    t.null
  ]),
  hotelsInSourceCity: t.union([t.array(HotelsInfoType), t.undefined]),
  hotelsInDestinationCity: t.union([t.array(HotelsInfoType), t.undefined]),
  holidayPackagesInSourceCity: t.union([t.array(HotelsInfoType), t.undefined]),
  hrPackages: t.union([t.array(t.string), t.undefined]),
  interlinks: t.union([t.undefined, t.array(InterlinkBlockDataChecker)]),

  holidayPackagesInDestinationCity: t.union([
    t.array(HotelsInfoType),
    t.undefined
  ]),
  showQuickBookCabsButton: t.boolean,
  showTripAssistantButton: t.boolean,
  showTripAssistantButtonForDweb: t.boolean,
  assistedFormOpenDelay: t.number,
  showWhatsappWidget: t.boolean,
  showCallUsWidget: t.boolean,
  assistantWidgetText: t.string,
  whatsappWidgetDeeplink: t.string,
  aggregateRating: AggregateRating,
  schemaMinFare: t.number,
  routeInfoData: t.any,
  customerCareNumber: t.string,
  semCustomerCareNumber: t.string,
  semInboundCallNumber: t.union([t.string, t.undefined]),
  showWhatsAppForAssistedForm: t.boolean,
  inboundCallText: t.union([t.string, t.undefined]),
  inboundCallNumber: t.union([t.string, t.undefined]),
  showInboundCallNumberInDesktop: t.boolean,
  showCallUsForAssistedForm: t.boolean,
  showCallusModalMweb: t.boolean,
  callusModalOpenDelay: t.number,
  showInboundNumberOnAssistedForm: t.boolean,
  couponInfo: CouponData,
  showLeaveIntentForm: t.boolean,
  packageData: t.any,
  allowRoundtrip: t.boolean,
  whatsappTooltipText: t.string,
  tripAssistanceToolTipText: t.string,
  openTripTypeAndThenDate: t.boolean,
  openDateWidget: t.boolean,
  openTripType: t.boolean,
  intentCaptureEnabled: t.union([t.string, t.undefined, t.null]),
  intentCaptureType: t.union([t.string, t.undefined, t.null]),
  anantmapUrl: t.union([t.string, t.undefined, t.null]),
  routeMapViewUrl: t.union([t.string, t.undefined, t.null]),
  ugcReviews: t.union([UgcReviewsData, t.undefined]),
  useAPILevelReviewSchema: t.boolean,
  tokenValues: t.union([t.any, t.undefined, t.null]),
  generatedSections: t.union([t.array(t.any), t.undefined, t.null]),
  uspBookingCountSummaryEnabled: t.boolean
});
export type ORDER_BY_PRICE_ASC_TYPE = typeof ORDER_BY_PRICE_ASC;
export type ORDER_BY_RATING_DESC_TYPE = typeof ORDER_BY_RATING_DESC;
export type sortOptionTypes =
  | ORDER_BY_PRICE_ASC_TYPE
  | ORDER_BY_RATING_DESC_TYPE;
export type RouteTemplateData = t.TypeOf<typeof RouteTemplateChecker>;
